import * as React from 'react';
import * as echarts from 'echarts/core';
// Import bar charts, all suffixed with Chart
import { LineChart } from 'echarts/charts';

// Import the title, tooltip, rectangular coordinate system, dataset and transform components
import {
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
} from 'echarts/components';
// Features like Universal Transition and Label Layout
import { LabelLayout, UniversalTransition } from 'echarts/features';

// Import the Canvas renderer
// Note that including the CanvasRenderer or SVGRenderer is a required step
import { SVGRenderer } from 'echarts/renderers';

import { format, parseISO } from 'date-fns';

import type {
    DatasetComponentOption,
    GridComponentOption,
    LineSeriesOption,
    TitleComponentOption,
    TooltipComponentOption,
} from 'echarts';

// Create an Option type with only the required components and charts via ComposeOption
type ECOption = echarts.ComposeOption<
    | LineSeriesOption
    | TitleComponentOption
    | TooltipComponentOption
    | GridComponentOption
    | DatasetComponentOption
>;

echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    LineChart,
    LabelLayout,
    UniversalTransition,
    SVGRenderer,
]);

interface SourceBreakdown {
    source: string;
    count: number;
}

interface TrendData {
    period: string;
    count: number;
    period_start: string;
    top_sources?: SourceBreakdown[];
}

interface CustomerTrendChartProps {
    data?: TrendData[];
    period: 'daily' | 'weekly' | 'monthly' | 'quarterly';
    seriesName?: string;
    valueLabel?: string;
}

const CustomerTrendChart = (props: CustomerTrendChartProps) => {
    const {
        data,
        period,
        seriesName = 'New Customers',
        valueLabel = 'customers',
    } = props;
    const chartRef = React.useRef<HTMLDivElement>(null);
    const chartInstance = React.useRef<echarts.ECharts | null>(null);

    React.useEffect(() => {
        if (!data || data.length === 0) {
            // Clear chart if no data
            if (chartInstance.current && chartRef.current) {
                chartInstance.current.setOption({
                    series: [{
                        data: []
                    }]
                });
            }
            return;
        }
        
        // Initialize chart
        if (chartRef.current) {
            if (!chartInstance.current) {
                chartInstance.current = echarts.init(chartRef.current);
            }

            // Format data for chart
            const chartData = data.map(item => {
                let date: Date;
                try {
                    // Try parsing as ISO first
                    if (item.period_start) {
                        date = parseISO(item.period_start);
                        // If parseISO returns invalid date, try new Date
                        if (isNaN(date.getTime())) {
                            date = new Date(item.period_start);
                        }
                    } else {
                        // Fallback: use current date if period_start is missing
                        date = new Date();
                    }
                } catch (e) {
                    // Fallback if parsing fails
                    console.warn('Error parsing date:', item.period_start, e);
                    date = new Date(item.period_start || new Date());
                }
                return [date.getTime(), item.count || 0];
            }).filter(item => !isNaN(item[0]) && !isNaN(item[1])); // Filter out invalid data points

            const topSourcesByTime = new Map<number, SourceBreakdown[]>();
            data.forEach((item) => {
                let date: Date;
                try {
                    if (item.period_start) {
                        date = parseISO(item.period_start);
                        if (isNaN(date.getTime())) {
                            date = new Date(item.period_start);
                        }
                    } else {
                        return;
                    }
                } catch (_e) {
                    return;
                }
                if (!isNaN(date.getTime()) && item.top_sources?.length) {
                    topSourcesByTime.set(date.getTime(), item.top_sources);
                }
            });

            // Configure the chart
            const option: ECOption = {
                xAxis: {
                    type: 'time',
                    axisLabel: {
                        formatter: (value: number) => {
                            const date = new Date(value);
                            if (period === 'daily') {
                                return format(date, 'MMM dd');
                            } else if (period === 'weekly') {
                                return format(date, 'MMM dd');
                            } else if (period === 'monthly') {
                                return format(date, 'MMM yyyy');
                            } else {
                                return format(date, 'QQQ yyyy');
                            }
                        },
                    },
                },
                yAxis: {
                    type: 'value',
                    axisLabel: {
                        formatter: (value: number) => `${value}`,
                    },
                    splitLine: {
                        show: true,
                        lineStyle: {
                            type: [3, 4],
                            color: '#aaa',
                        },
                    },
                },
                tooltip: {
                    trigger: 'axis',
                    formatter: (params: any) => {
                        const param = params[0];
                        const date = new Date(param.value[0]);
                        let dateStr = '';
                        if (period === 'daily') {
                            dateStr = format(date, 'MMM dd, yyyy');
                        } else if (period === 'weekly') {
                            dateStr = format(date, 'MMM dd, yyyy');
                        } else if (period === 'monthly') {
                            dateStr = format(date, 'MMMM yyyy');
                        } else {
                            dateStr = format(date, 'QQQ yyyy');
                        }
                        let line = `${dateStr}: ${param.value[1]} ${valueLabel}`;
                        const sources = topSourcesByTime.get(param.value[0]);
                        if (sources?.length) {
                            line += '<br/><span style="font-size:11px;opacity:0.85">Top sources (first visit):</span>';
                            sources.forEach((s) => {
                                line += `<br/><span style="font-size:11px">• ${s.source}: ${s.count}</span>`;
                            });
                        }
                        return line;
                    },
                    axisPointer: {
                        type: 'line',
                        lineStyle: {
                            type: [3, 3],
                        },
                    },
                },
                grid: {
                    left: '0%',
                    right: '1%',
                    bottom: '0%',
                    top: '2%',
                    containLabel: true,
                },
                series: [
                    {
                        name: seriesName,
                        type: 'line',
                        smooth: true,
                        smoothMonotone: 'x',
                        symbol: 'circle',
                        symbolSize: 6,
                        sampling: 'average',
                        areaStyle: {
                            color: new echarts.graphic.LinearGradient(
                                0,
                                0,
                                0,
                                1,
                                [
                                    {
                                        offset: 0.05,
                                        color: 'rgba(136, 132, 216, 0.8)',
                                    },
                                    {
                                        offset: 0.95,
                                        color: 'rgba(136, 132, 216, 0)',
                                    },
                                ]
                            ),
                        },
                        lineStyle: {
                            color: '#8884d8',
                            width: 2,
                        },
                        data: chartData,
                    },
                ],
            };

            // Apply the config
            chartInstance.current.setOption(option);
        }

        // Handle resize
        const handleResize = () => {
            chartInstance.current?.resize();
        };

        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => {
            window.removeEventListener('resize', handleResize);
            if (chartInstance.current) {
                chartInstance.current.dispose();
                chartInstance.current = null;
            }
        };
    }, [data, period, seriesName, valueLabel]);

    return <div ref={chartRef} style={{ width: '100%', height: 300 }} />;
};

export default CustomerTrendChart;

